import { environment } from 'src/environments/environment';

/** The non-URL sentinel galactus-core stores for a card it has no picture for. */
export const NO_IMAGE_SENTINEL = 'cardImageNotAvailable';

/**
 * Does this stored value name a picture at all?
 *
 * The backend contract is nullable (`product.image`, serialised as `imageName` and again as
 * `cardData.image`), and "no picture" arrives in four spellings: null/undefined, an empty or
 * blank string, the `cardImageNotAvailable` sentinel, and a non-string a malformed row carried.
 * THE SENTINEL IS TRUTHY, so every site that asks `item.cardData?.image ? ... : placeholder`
 * answers yes to a row with no picture and renders a picture-shaped fallback, while a null row
 * one line over renders an icon -- two renderings of one condition. Ask this instead.
 */
export function hasStoredImage(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return trimmed !== '' && trimmed !== NO_IMAGE_SENTINEL;
}

/**
 * Resolve a stored product image URL to something a `<img src>` on THIS origin can load.
 *
 * galactus-core stores `product.image` (serialised as `imageName`, and again as
 * `cardData.image`) RELATIVE to the API gateway since the kiosk image mirror landed:
 * `/api/v2/image/mirror/<token>/<path>`. That is correct wherever the origin that serves the
 * page also serves `/api` -- the kiosk vhost, `ui.<domain>`, and the ng dev-server, whose proxy
 * owns `/api` -- and wrong everywhere this app is actually deployed: the FE container's nginx
 * serves static files only, `apiUrl` is absolute (`https://ui.<domain>/api`), so a bare
 * `/api/...` asks the FE origin, `try_files` answers with `index.html`, and the tile is broken.
 *
 * The rule is derived from `apiUrl`, not hardcoded: the value's leading segment must equal
 * apiUrl's own path prefix (`/api`), and that prefix is swapped for the whole `apiUrl`. So:
 *
 * - `apiUrl = 'https://ui.galactus.it/api'` -> `https://ui.galactus.it/api/v2/image/mirror/...`
 * - `apiUrl = '/api'` (development, local)   -> unchanged, the proxy takes it from here
 *
 * Anything else passes through untouched: an absolute URL (CDN rows, the legacy absolute
 * `ui.<domain>` rows the backfill left alone) and the bundled `/assets/...` placeholder -- which
 * is served by the FE origin, so re-rooting it onto the API would break the fallback.
 *
 * The value is TRIMMED before every one of those tests, and the trimmed form is what comes back.
 * A stored `' /api/v2/image/mirror/x.jpg'` used to clear the empty/sentinel test on its trimmed
 * form and then fail the prefix test on its padded one, so it was returned unrooted and bound to
 * `[src]` -- the exact broken tile this function exists to prevent, arriving through whitespace.
 *
 * IT ALWAYS ANSWERS A STRING A TILE CAN LOAD: anything `hasStoredImage` refuses resolves to
 * `environment.defaultImageMagic`. That is deliberately decided HERE rather than at each render
 * site: a caller that saw the raw value either hid the tile behind an `*ngIf` or bound `[src]`
 * to it, and Angular sanitises a null through `String(value)`, so the browser requested the
 * literal `"null"` against the page origin and painted a broken glyph. The `(error)` handlers
 * that used to rescue some of those sites are now a second line of defence against a URL that is
 * well-formed but 404s, not the mechanism.
 *
 * This is the ONLY place that conversion lives. Every render site binds through it (the
 * `appImageUrl` pipe, or this function from TypeScript). A sheet is NOT a render site -- see
 * `exportImageUrl`, which answers an absolute URL or an empty cell and never the placeholder.
 */
export function resolveImageUrl(value: unknown, apiUrl: string = environment.apiUrl): string {
  if (!hasStoredImage(value)) {
    return environment.defaultImageMagic;
  }
  const url = value.trim();
  const base = apiUrl.replace(/\/+$/, '');
  const prefix = apiPathPrefix(base);
  if (prefix === base) {
    // apiUrl is itself relative: the page origin already serves the prefix (dev proxy), and
    // prefixing again would only turn `/api/x` into `/api/x` anyway.
    return url;
  }
  return url.startsWith(prefix + '/') ? base + url.slice(prefix.length) : url;
}

/**
 * The image URL for a CSV/XLSX cell: ALWAYS ABSOLUTE, or empty when the row has no picture.
 *
 * A sheet is read outside the app -- in Excel, in a mail client, by a script -- where nothing
 * resolves a relative path, so every one of the three ways `resolveImageUrl` can answer a
 * relative string is wrong here: `apiUrl` is relative under `development`/`local` (the dev proxy
 * owns `/api` on the page origin, a sheet has no proxy), the bundled `/assets/...` placeholder is
 * relative by construction, and a legacy stored row can be root-relative to something else. So
 * the resolved value is re-based on the page origin, which is also what makes a `//cdn/x` row a
 * real URL rather than a protocol-relative fragment.
 *
 * A row with NO picture exports an EMPTY CELL, never the placeholder. The placeholder is a
 * rendering decision -- it says "no image" on a tile -- and a sheet has no tile: exporting it
 * puts the same dead-looking link on every image-less row, indistinguishable from a real image
 * URL when the reader scans, sorts or de-duplicates the column. Emptiness is what a sheet spells
 * "no value" with.
 */
export function exportImageUrl(
  value: unknown,
  apiUrl: string = environment.apiUrl,
  origin: string = window.location.origin
): string {
  if (!hasStoredImage(value)) {
    return '';
  }
  return absolutise(resolveImageUrl(value, apiUrl), origin);
}

/** `/x.jpg` -> `<origin>/x.jpg`; an already-absolute URL (and a `data:` URI) is returned as-is. */
function absolutise(url: string, origin: string): string {
  try {
    return new URL(url, origin).href;
  } catch {
    // A base the URL parser refuses (no origin at all in some sandboxed contexts). Better a
    // relative cell than a thrown export.
    return url;
  }
}

/** The path part of `apiUrl` with no trailing slash: `https://h/api` -> `/api`, `/api` -> `/api`, `https://h` -> ``. */
function apiPathPrefix(apiUrl: string): string {
  return apiUrl.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
}
